import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { CaptionMD, ParagraphLG } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import { useGuildMember } from '@/services/GuildUserService'
import { FeaturedProject } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { FeaturedProjectCarousel } from './FeaturedProjectCarousel'
import { FeaturedProjectGuildButtonGroup } from './FeaturedProjectGuildButtonGroup'
import { FeaturedProjectGuildLabel } from './FeaturedProjectGuildLabel'
import { FeaturedProjectLabel } from './FeaturedProjectLabel'
import { FeaturedProjectLearnMoreButtonGroup } from './FeaturedProjectLearnMoreButtonGroup'
import { FeaturedProjectTicketsButtonGroup } from './FeaturedProjectTicketsButtonGroup'
import { FeaturedProjectWatchButtonGroup } from './FeaturedProjectWatchButtonGroup'

interface FeaturedProjectCallToActionProps {
  featuredProject: FeaturedProject
  position?: string
  shorterDisplay?: boolean
  videoBackground?: boolean
  contentInteraction?: DiscoverItemInteraction | null
  projectSlug?: Maybe<string>
}

export const FeaturedProjectCallToAction: React.FC<FeaturedProjectCallToActionProps> = ({
  featuredProject,
  position,
  shorterDisplay = false,
  videoBackground = false,
  contentInteraction,
  projectSlug,
}) => {
  const { isSubscribedToPifOrGuild, isGuildMember } = useGuildMember()
  const {
    heading,
    guildHeading,
    logoHeight,
    logoUrl,
    logoWidth,
    pillLabel,
    showGuildCta,
    showTicketsCta,
    subHeading,
    showWatchCta,
    showLearnMoreCta,
  } = featuredProject
  const isDavid = projectSlug === slugs.david
  const imageHeight = getImageHeight(videoBackground, shorterDisplay, logoHeight)
  const imageWidth = getImageWidth(videoBackground, shorterDisplay, logoWidth)

  return (
    <div className={getContainerClasses({ position, videoBackground, shorterDisplay })}>
      <div
        className={classNames(
          'flex w-full flex-col items-center gap-4 pb-12 text-center lg:w-fit lg:items-start lg:pb-8',
          {
            'sm:mb-10': !shorterDisplay,
          },
        )}
      >
        {showGuildCta && <FeaturedProjectGuildLabel />}
        <div className="flex w-full items-center justify-center">
          <Image
            alt="Featured Project"
            className="mb-1 !max-w-full"
            src={getCloudinaryImageUrl({
              path: logoUrl ?? '',
              transforms: 'e_trim',
            })}
            style={{ maxHeight: imageHeight, maxWidth: imageWidth }}
            width={imageWidth}
            height={imageHeight}
          />
        </div>
        <div className="w-full text-center">
          <When condition={isGuildMember}>
            {(heading || guildHeading) && (
              <ParagraphLG
                color="white"
                className={classNames({ '[text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)]': videoBackground })}
              >
                {guildHeading ?? heading}
              </ParagraphLG>
            )}
          </When>
          <When condition={!isGuildMember}>{heading && <ParagraphLG color="white">{heading}</ParagraphLG>}</When>
          {subHeading && <CaptionMD color="white">{subHeading}</CaptionMD>}
        </div>
        {pillLabel && <FeaturedProjectLabel featuredProject={featuredProject} />}
        {showGuildCta && (
          <FeaturedProjectGuildButtonGroup
            {...featuredProject}
            isSubscribedToPifOrGuild={isSubscribedToPifOrGuild}
            isGuildMember={isGuildMember}
            showDavidVideo={videoBackground && isDavid}
            contentInteraction={contentInteraction}
          />
        )}
        {showTicketsCta && (
          <FeaturedProjectTicketsButtonGroup
            {...featuredProject}
            showDavidVideo={videoBackground && isDavid}
            contentInteraction={contentInteraction}
          />
        )}
        {showWatchCta && (
          <FeaturedProjectWatchButtonGroup {...featuredProject} showDavidVideo={videoBackground && isDavid} />
        )}
        {showLearnMoreCta && <FeaturedProjectLearnMoreButtonGroup {...featuredProject} />}
        <FeaturedProjectCarousel featuredProject={featuredProject} />
      </div>
    </div>
  )
}

const getContainerClasses = ({
  position,
  videoBackground,
  shorterDisplay,
}: {
  position?: string
  videoBackground: boolean
  shorterDisplay: boolean
}): string => {
  const baseClasses =
    'absolute flex w-full left-1/2 max-w-[800px] -translate-x-1/2 flex-col items-center justify-center px-6'

  const backgroundClasses = {
    'bottom-0 pb-8 sm:pb-0': !videoBackground,
    'top-1/2 -translate-y-1/2': videoBackground,
    'lg:bottom-0': position === 'center' && !videoBackground,
  }

  const leftPositionClasses = position === 'left' && {
    'lg:bottom-0 lg:left-24 lg:right-auto -translate-x-1/2 lg:translate-x-0 2xl:left-40': !videoBackground,
    'lg:top-1/2 lg:left-24 lg:right-auto lg:-translate-y-1/2 lg:translate-x-0 2xl:left-40': videoBackground,
    'lg:-translate-y-1/2 2xl:bottom-12': !shorterDisplay && !videoBackground,
    'lg:-translate-y-1/3': shorterDisplay && !videoBackground,
  }

  const rightPositionClasses = position === 'right' && {
    'lg:bottom-0 lg:left-auto lg:right-24 -translate-x-1/2 lg:-translate-x-0 2xl:right-40': !videoBackground,
    'lg:top-1/2 lg:left-auto lg:right-24 lg:-translate-y-1/2 lg:-translate-x-0 2xl:right-40': videoBackground,
    'lg:-translate-y-1/2 2xl:bottom-12': !shorterDisplay && !videoBackground,
    'lg:-translate-y-1/3': shorterDisplay && !videoBackground,
  }

  return classNames(baseClasses, backgroundClasses, leftPositionClasses, rightPositionClasses)
}

const getImageHeight = (videoBackground: boolean, shorterDisplay: boolean, logoHeight?: Maybe<number>): number => {
  const height = logoHeight ?? 0

  if (videoBackground) {
    return height / 1.5
  }

  if (shorterDisplay) {
    return height / 3
  }

  return height
}

const getImageWidth = (videoBackground: boolean, shorterDisplay: boolean, logoWidth?: Maybe<number>): number => {
  const width = logoWidth ?? 0

  if (videoBackground) {
    return width / 1.5
  }

  if (shorterDisplay) {
    return width / 3
  }

  return width
}
